import { z } from 'zod';

/**
 * Create Domain Request DTO
 * 
 * Validates client input for domain creation
 */
export const CreateDomainRequestSchema = z.object({
    name: z.string()
        .min(2, 'Domain name must be at least 2 characters')
        .max(100, 'Domain name must be less than 100 characters')
        .regex(/^[a-zA-Z][a-zA-Z0-9\s-]*$/, 'Domain name must start with a letter and contain only letters, numbers, spaces, and hyphens'),
    description: z.string()
        .min(10, 'Description must be at least 10 characters')
        .max(1000, 'Description must be less than 1000 characters'),
    vision: z.string()
        .min(20, 'Vision must be at least 20 characters')
        .max(500, 'Vision must be less than 500 characters'),
    projectId: z.string().uuid('Invalid project ID format')
});

export type CreateDomainRequest = z.infer<typeof CreateDomainRequestSchema>;

/**
 * Update Domain Request DTO
 */
export const UpdateDomainRequestSchema = z.object({
    name: z.string()
        .min(2, 'Domain name must be at least 2 characters')
        .max(100, 'Domain name must be less than 100 characters')
        .regex(/^[a-zA-Z][a-zA-Z0-9\s-]*$/, 'Domain name must start with a letter and contain only letters, numbers, spaces, and hyphens')
        .optional(),
    description: z.string()
        .min(10, 'Description must be at least 10 characters')
        .max(1000, 'Description must be less than 1000 characters')
        .optional(),
    vision: z.string()
        .min(20, 'Vision must be at least 20 characters')
        .max(500, 'Vision must be less than 500 characters')
        .optional(),
    status: z.enum(['draft', 'active', 'deprecated']).optional()
});

export type UpdateDomainRequest = z.infer<typeof UpdateDomainRequestSchema>;

/**
 * Get Domains Query Parameters DTO
 */
export const GetDomainsQuerySchema = z.object({
    projectId: z.string().uuid().optional(),
    status: z.enum(['draft', 'active', 'deprecated', 'all']).default('active'),
    limit: z.coerce.number().min(1).max(100).default(20),
    offset: z.coerce.number().min(0).default(0),
    search: z.string().max(100).optional(),
    includeStats: z.coerce.boolean().default(false)
});

export type GetDomainsQuery = z.infer<typeof GetDomainsQuerySchema>;

/**
 * Domain Parameters DTO
 */
export const DomainParamsSchema = z.object({
    domainId: z.string().uuid('Invalid domain ID format')
});

export type DomainParams = z.infer<typeof DomainParamsSchema>;